import { useCallback, useState } from "react";

/**
 * Shared selection state for Library Bulk Edit — used identically by both
 * Grid and List views (same object, no per-view copy) so switching view mode
 * never loses the selection.
 *
 * Two modes, matching the backend's PATCH admin/libraries/bulk contract:
 * - "ids": an explicit set of selected record ids.
 * - "allFiltered": every record matching the currently applied filter,
 *   minus whatever the admin has since unchecked (excludedIds). Entered via
 *   selectAllFiltered() after the admin selects everything currently loaded
 *   and chooses to escalate to the full filtered result set.
 */
export function useBulkSelection() {
  const [mode, setMode] = useState("ids");
  const [selectedIds, setSelectedIds] = useState([]);
  const [excludedIds, setExcludedIds] = useState([]);
  const [totalFiltered, setTotalFiltered] = useState(0);

  const toggleOne = useCallback((id, checked) => {
    if (!id) return;
    if (mode === "allFiltered") {
      setExcludedIds((prev) => {
        if (checked) return prev.filter((x) => x !== id);
        return prev.includes(id) ? prev : [...prev, id];
      });
      return;
    }
    setSelectedIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  }, [mode]);

  // Select/deselect a batch of ids at once (header checkbox on the currently
  // loaded/rendered records). Only meaningful in "ids" mode.
  const selectMany = useCallback((ids, checked) => {
    setMode("ids");
    setExcludedIds([]);
    setSelectedIds((prev) => {
      if (checked) {
        const merged = new Set(prev);
        ids.forEach((id) => id && merged.add(id));
        return Array.from(merged);
      }
      const remove = new Set(ids);
      return prev.filter((id) => !remove.has(id));
    });
  }, []);

  const selectAllFiltered = useCallback((total) => {
    setMode("allFiltered");
    setExcludedIds([]);
    setTotalFiltered(total);
  }, []);

  const clear = useCallback(() => {
    setMode("ids");
    setSelectedIds([]);
    setExcludedIds([]);
    setTotalFiltered(0);
  }, []);

  const isSelected = useCallback(
    (id) => (mode === "allFiltered" ? !excludedIds.includes(id) : selectedIds.includes(id)),
    [mode, selectedIds, excludedIds],
  );

  const count = mode === "allFiltered" ? Math.max(totalFiltered - excludedIds.length, 0) : selectedIds.length;

  return {
    mode,
    selectedIds,
    excludedIds,
    totalFiltered,
    count,
    toggleOne,
    selectMany,
    selectAllFiltered,
    clear,
    isSelected,
  };
}
